import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { User } from '../../interfaces/common/authentication';
import { jwtDecode } from 'jwt-decode';
import axios, { isAxiosError } from 'axios';
import { API_ENDPOINT } from '../../configurations/global.config';
import { useSnackBar } from '../snackbar';
import { UserType } from '../../enums/users';
import { APP_VERSION_HEADER } from '../../utils/app_authentication';

type Props = {
  children?: ReactNode;
};

type IAuthContext = {
  user: User | null;
  setUser: (user: User | null) => void;
  refreshUser: () => Promise<void>;
  config: {
    headers?: { Authorization: string; 'X-Frontend-Version'?: string };
  };
};

const initialValue: IAuthContext = {
  user: null,
  setUser: () => {
    /* unimplemented initially */
  },
  refreshUser: async () => {
    /* unimplemented initially */
  },
  config: {},
};

const AuthContext = createContext<IAuthContext>(initialValue);

const AuthProvider = ({ children }: Props) => {
  //Initializing an auth state with false value (unauthenticated)
  const [user, setUser] = useState<User | null>(() => {
    // Initialize user state with the data from localStorage (if available)
    const storedUser = localStorage.getItem('pt-user');
    return storedUser ? JSON.parse(storedUser) : null;
  });

  const { showMessage } = useSnackBar();

  const refreshUser = async (allow_fail = false) => {
    if (!user) return;

    try {
      const response = await axios.get(`${API_ENDPOINT}/api/auth/user/`, {
        headers: {
          Authorization: `Bearer ${user.authToken}`,
          ...APP_VERSION_HEADER,
        },
      });

      const data = response.data;
      console.log('data = ', data);

      const authenticationData: User = {
        ...user,
        name: data.user.name,
        phone_number: data.user.phone_number,
        type: data.user.type,
        product_price_enabled: data.user.product_price_enabled,
        is_admin:
          data.user.is_admin || data.user.type === UserType.ORGANISATION,
      };

      if (authenticationData.type === UserType.ORGANISATION) {
        authenticationData.organisation = {
          pk: data.user.id,
          org_id: data.user.organisation_id,
        };
        authenticationData.branches = data.user.branches || [];
      }

      if (authenticationData.type === UserType.EMPLOYEE) {
        authenticationData.branches = data.user.branches;

        authenticationData.organisation = {
          pk: data.user.organisation.id,
          org_id: data.user.organisation.organisation_id,
        };

        authenticationData.roles = data.user.roles;
      }
      if (
        authenticationData.type === UserType.MERCHANT ||
        authenticationData.type === UserType.DRIVER
      ) {
        authenticationData.organisation = {
          pk: data.user.organisation.id,
          org_id: data.user.organisation.organisation_id,
        };

        authenticationData.default_delivery_fee =
          data.user.default_delivery_fee;
        authenticationData.address = data.user.address;
        authenticationData.storage_key = data.user.storage_key;
      }

      if (authenticationData.type === UserType.DRIVER) {
        authenticationData.connected_to_whatsapp =
          data.user?.connected_to_whatsapp;
      }

      setUser(authenticationData);
    } catch (err) {
      console.error('Failed to refresh user', err);
      if (!allow_fail || (isAxiosError(err) && err.response?.data.code == "token_not_valid")) {
        showMessage('Failed to refresh your data. Please login again.', 'error');
        setUser(null);
      }
    }
  };

  useEffect(() => {
    localStorage.setItem('pt-user', JSON.stringify(user));
  }, [user]);

  useEffect(() => {
    refreshUser(true)
  }, [])

  useEffect(() => {
    if (user) {
      const token = user.authToken;
      const { exp } = jwtDecode(token);

      if (exp) {
        const expirationTime = exp * 1000 - 60000;
        if (Date.now() >= expirationTime) {
          setUser(null);
        }
      }
    }
  });

  const config = {
    headers: {
      Authorization: `Bearer ${user?.authToken}`,
      ...APP_VERSION_HEADER,
    },
  };

  console.log('config = ', config);

  return (
    <AuthContext.Provider value={{ user, setUser, refreshUser, config }}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider };
